import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ReceiptView } from "@/components/admin/AdminReceipts";
import { Pills } from "@/components/admin/ReceiptForm";
import { receiptWhen } from "@/lib/receipt";

const SIDES = [{ value: "General Council", label: "General Council", key: "gc" }, { value: "MWJK", label: "MWJK", key: "mwjk" }];

export default function MyReceipts() {
  const { can, settings, myChurch } = useApp();
  const sides = SIDES.filter((s) => can(`church_view_receipts_${s.key}`) && settings?.features?.[`receipts_${s.key}`] !== false);
  const [dest, setDest] = useState("");
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  const side = sides.find((s) => s.value === dest) ? dest : sides[0]?.value || "";
  useEffect(() => {
    if (!side) { setRows([]); return; }
    api.get("/receipts/mine", { params: { destination: side } }).then((r) => setRows(r.data)).catch((e) => { setRows([]); toast.error(errMsg(e)); });
  }, [side]);
  return (
    <div className="space-y-4" data-testid="my-receipts">
      <PageHeader eyebrow={myChurch?.name || "My church"} title="My receipts" subtitle="View, print or download your church's receipts." />
      {!sides.length ? <p className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-amber-900" data-testid="my-receipts-off">Receipts are not open to churches right now.</p> : (
        <>
          {sides.length > 1 && <div className="inline-block rounded-full bg-white p-1.5"><Pills value={side} onChange={setDest} options={sides} testid="my-receipts-side" /></div>}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="my-receipts-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["No.", "Date", "Paid by", "For", "Event", "Total", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {!rows.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="my-receipts-empty">No receipts yet on {side}.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`my-receipt-row-${r.number}`}>
                    <td className="px-4 py-3 font-bold">{r.number}</td><td className="px-4 py-3">{receiptWhen(r)}</td><td className="px-4 py-3">{r.payer_name}</td>
                    <td className="px-4 py-3">{r.payment_label}</td><td className="px-4 py-3">{r.event_name || "—"}</td><td className="px-4 py-3 font-semibold">{money(r.total)}</td>
                    <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => setView(r)} data-testid={`my-receipt-open-${r.number}`}><Eye className="mr-1 h-4 w-4" /> Open</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <ReceiptView r={view} onClose={() => setView(null)} />
    </div>
  );
}
